import { useMemo, useState } from 'react';
import type { CatalogModifierGroup, CatalogProduct } from '@dastarhan/contracts';
import { formatMoney } from '@dastarhan/core';
import type { ItemModifier } from '@dastarhan/core';
import { z } from 'zod';

type Group = z.infer<typeof CatalogModifierGroup>;
type Product = z.infer<typeof CatalogProduct>;

/**
 * Выбор модификаторов перед добавлением в чек.
 * min=max=1 — переключатель; иначе — до maxSelect отметок.
 * «Добавить» недоступна, пока обязательные группы не выбраны.
 */
export function ModifierDialog({ product, groups, onConfirm, onCancel }: {
  product: Product;
  groups: Group[];
  onConfirm: (mods: ItemModifier[]) => void;
  onCancel: () => void;
}) {
  const [picked, setPicked] = useState<Record<string, string[]>>(() => {
    const init: Record<string, string[]> = {};
    for (const g of groups) {
      // Обязательный одиночный выбор — сразу первый вариант:
      // кассиру остаётся поменять, а не вспоминать, что нужно выбрать
      init[g.id] = g.minSelect >= 1 && g.maxSelect === 1 && g.options[0]
        ? [g.options[0].id] : [];
    }
    return init;
  });

  const toggle = (g: Group, optionId: string) => {
    setPicked((prev) => {
      const cur = prev[g.id] ?? [];
      if (g.maxSelect === 1) return { ...prev, [g.id]: [optionId] };
      if (cur.includes(optionId)) return { ...prev, [g.id]: cur.filter((x) => x !== optionId) };
      if (cur.length >= g.maxSelect) return prev; // лимит — лишний тап игнорируем
      return { ...prev, [g.id]: [...cur, optionId] };
    });
  };

  const valid = groups.every((g) => (picked[g.id] ?? []).length >= g.minSelect);

  const mods: ItemModifier[] = useMemo(() => {
    const out: ItemModifier[] = [];
    for (const g of groups) {
      for (const id of picked[g.id] ?? []) {
        const o = g.options.find((x) => x.id === id);
        if (o) out.push({ name: o.name, priceDelta: o.priceDelta, cost: o.cost,
          ...(o.removes ? { removes: o.removes } : {}) });
      }
    }
    return out;
  }, [groups, picked]);

  const delta = mods.reduce((s, m) => s + m.priceDelta, 0);

  return (
    <div className="dialog-backdrop" role="dialog" aria-modal="true" aria-label={product.name}>
      <div className="dialog mod-dialog">
        <h1>{product.name}</h1>
        {groups.map((g) => (
          <fieldset key={g.id} className="mod-group">
            <legend>
              {g.name}
              <span className="mod-rule">
                {g.minSelect >= 1 ? ' · обязательно' : g.maxSelect > 1 ? ` · до ${g.maxSelect}` : ''}
              </span>
            </legend>
            <div className="mod-options">
              {g.options.map((o) => {
                const on = (picked[g.id] ?? []).includes(o.id);
                return (
                  <button key={o.id} className={`btn mod ${on ? 'on' : ''}`}
                    aria-pressed={on} onClick={() => toggle(g, o.id)}>
                    <span>{o.name}</span>
                    {o.priceDelta !== 0 && (
                      <span className="money">+{formatMoney(o.priceDelta)}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </fieldset>
        ))}
        <div className="mod-actions">
          <button className="btn ghost" onClick={onCancel}>Отмена</button>
          <button className="btn accent" disabled={!valid} onClick={() => onConfirm(mods)}>
            В чек · <span className="money">{formatMoney(product.price + delta)}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
